import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import {
  GetCustomisationParameterApiResponse,
  CustomisationTypeValue,
} from "../../interfaces/business";
import { Button } from "../Base/Button";
import { Input } from "../Base/Input";
import { Select } from "../Base/Select";
import CustomizationScheduleList from "../CustomizationScheduleList";
import OverlaySpinner from "../OverlaySpinner";
import { Toast } from "../Base/toast";
import { deepCompareObjects } from "../../utils";

// const recurringBookingOptions = ["DAILY", "WEEKLY", "MONTHLY"] as const;

interface FormState {
  accentColor: string;
  minTimeToBook: {
    unit: string;
    value: number;
  };
  timeSlotInterval: number;
  recurringOptions: string[];
  depositPercentage: number;
  daysBeforeNoRefund: number;
}

const BusinessCustomization = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [initialData, setInitialData] = useState<CustomisationTypeValue>();
  const [formState, setFormState] = useState<FormState>({
    accentColor: "#000000",
    minTimeToBook: { unit: "hours", value: 24 },
    timeSlotInterval: 15,
    recurringOptions: [],
    depositPercentage: 100,
    daysBeforeNoRefund: 0,
  });
  const [schedule, setSchedule] = useState<any[]>([]);

  const hasChanges = useMemo(() => {
    if (!initialData) return false;

    return !deepCompareObjects(formState, {
      ...initialData,
      depositPercentage: initialData.depositPercentage || 100,
    });
  }, [formState, initialData]);

  useEffect(() => {
    const fetchCustomizationParameters = async () => {
      try {
        setIsLoading(true);
        const {
          data: { data: customizationData },
        } = await axios.get<GetCustomisationParameterApiResponse>(
          `/api/business/parameter/customisation`
        );

        const {
          schedule,
          accentColor,
          minTimeToBook,
          timeSlotInterval,
          recurringOptions,
          depositPercentage,
          daysBeforeNoRefund,
        } = customizationData.value;
        setInitialData(customizationData.value);
        setSchedule(schedule);
        setFormState({
          accentColor,
          minTimeToBook,
          timeSlotInterval,
          recurringOptions: recurringOptions ?? [],
          depositPercentage: depositPercentage ?? 100,
          daysBeforeNoRefund: daysBeforeNoRefund ?? 0,
        });
      } catch (error) {
        Toast("Failed to load customization settings", "error");
        console.error("Fetch error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomizationParameters();
  }, []);

  // const handleRecurringOptionToggle = (option: string) => {
  //   setFormState((prev) => ({
  //     ...prev,
  //     recurringOptions: prev.recurringOptions.includes(option)
  //       ? prev.recurringOptions.filter((o) => o !== option)
  //       : [...prev.recurringOptions, option],
  //   }));
  // };

  const handleScheduleUpdate = (payload: any, index: number) => {
    const updatedSchedule = schedule.map((item, i) =>
      i === index ? { ...item, ...payload } : item
    );
    setSchedule(updatedSchedule);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!initialData || !hasChanges) return;

    try {
      setIsLoading(true);
      const payload = {
        ...initialData,
        ...formState,
        schedule,
        depositPercentage: formState.depositPercentage,
      };

      await axios.put(`/api/business/parameter/customisation`, payload);
      setInitialData({
        ...payload,
      });
      Toast("Settings updated successfully", "success");
    } catch (error) {
      Toast("Failed to update settings", "error");
      console.error("Update error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex justify-center">
      <div className="w-11/12 p-6 my-10 bg-white md:w-8/12 lg:p-8 rounded-xl shadow-lg">
        <OverlaySpinner isLoading={isLoading}>
          <form onSubmit={handleSubmit} className="space-y-6 font-roboto">
            {/* Accent Color */}
            <div className="flex items-center gap-4">
              <Input
                label="Accent Color"
                type="color"
                value={formState.accentColor}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    accentColor: e.target.value,
                  }))
                }
                className="w-1/2"
              />
              <div
                className="mt-4 w-8 h-8 rounded border-2 border-gray-200"
                style={{ backgroundColor: formState.accentColor }}
              />
            </div>

            {/* Recurring Options */}
            {/* <div className="space-y-2">
              <label className="block text-sm font-semibold">
                Recurring Booking Options
                <span className="block text-xs font-normal text-gray-600">
                  Select available booking frequencies
                </span>
              </label>
              <div className="flex gap-3 flex-wrap">
                {recurringBookingOptions.map((option) => (
                  <button
                    type="button"
                    key={option}
                    onClick={() => handleRecurringOptionToggle(option)}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      formState.recurringOptions.includes(option)
                        ? "bg-primary text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div> */}

            {/* Time Settings */}
            <div className="flex justify-between gap-4">
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  value={formState.minTimeToBook?.value}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      minTimeToBook: {
                        ...prev.minTimeToBook,
                        value: Number(e.target.value),
                      },
                    }))
                  }
                  label="Minimum Booking Notice"
                />
                <Select
                  value={formState.minTimeToBook?.unit}
                  options={[
                    { value: "minutes", text: "Minutes" },
                    { value: "hours", text: "Hours" },
                    { value: "days", text: "Days" },
                  ]}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      minTimeToBook: {
                        ...prev.minTimeToBook,
                        unit: e.target.value,
                      },
                    }))
                  }
                  className="w-full h-full"
                  label="Select Time Unit"
                />
              </div>

              <Input
                label="Appointment Slot Duration (minutes)"
                type="number"
                value={formState.timeSlotInterval}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    timeSlotInterval: Number(e.target.value),
                  }))
                }
                className="w-1/2"
                // min="1"
              />
            </div>

            {/* Deposit Settings */}
            <div className="flex gap-4">
              <Input
                label="Deposit Percentage"
                type="number"
                value={formState.depositPercentage}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    depositPercentage: Math.min(
                      100,
                      Math.max(0, Number(e.target.value))
                    ),
                  }))
                }
                className="w-1/2"
              />
              <Input
                label="Days Before No Refund"
                type="number"
                value={formState.daysBeforeNoRefund}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    daysBeforeNoRefund: Math.max(0, Number(e.target.value)),
                  }))
                }
                className="w-1/2"
              />
            </div>

            {/* Schedule Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Business Hours Schedule</h3>
              {schedule.map((scheduleItem, index) => (
                <CustomizationScheduleList
                  key={scheduleItem.day || index}
                  schedule={scheduleItem}
                  update={(payload) => handleScheduleUpdate(payload, index)}
                />
              ))}
            </div>

            <Button
              type="submit"
              disabled={!hasChanges || isLoading}
              // className={`w-full md:w-auto ${
              //   hasChanges ? "bg-primary text-white" : "bg-gray-200"
              // }`}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </OverlaySpinner>
      </div>
    </div>
  );
};

export default BusinessCustomization;
